import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle,
  DownloadSimple,
  Info,
  Plus,
  UploadSimple,
  Users,
} from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useGet } from "@/hooks/useResource";
import { Loading, PageHeader, EmptyState, ConfirmationStamp, StatusBadge } from "@/components/common";
import { GuestFormDialog, BLANK_GUEST } from "@/components/guests/GuestFormDialog";
import { GuestNumbersPanel } from "@/components/guests/GuestNumbersPanel";
import { GuestRow } from "@/components/guests/GuestRow";
import { GUEST_TYPES, guestType } from "@/lib/guests";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EVENTS_EMAIL = "events@gledswood.com.au";

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function Guests() {
  const { data: guests, loading, refetch } = useGet("/client/guests");
  const { data: stored, refetch: refetchNumbers } = useGet("/client/guest-numbers");
  const fileRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK_GUEST);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState("");

  const list = useMemo(() => guests || [], [guests]);
  const locked = stored?.client_status === "confirmed";

  const typeCounts = useMemo(() => {
    const counts = {};
    GUEST_TYPES.forEach((t) => {
      counts[t.id] = list.filter((g) => guestType(g) === t.id).length;
    });
    return counts;
  }, [list]);
  const withDietary = list.filter((g) => g.dietary && g.dietary !== "none").length;

  const openAdd = () => {
    setEditing(null);
    setForm(BLANK_GUEST);
    setDialogOpen(true);
  };
  const openEdit = (guest) => {
    setEditing(guest);
    setForm({ ...BLANK_GUEST, ...guest, type: guestType(guest) });
    setDialogOpen(true);
  };

  const saveGuest = async () => {
    if (!form.name.trim()) {
      toast.error("Please enter a guest name.");
      return;
    }
    setSaving("guest");
    try {
      const payload = { ...form, name: form.name.trim() };
      if (editing) {
        await api.put(`/client/guests/${editing.id}`, payload);
        toast.success("Guest updated.");
      } else {
        await api.post("/client/guests", payload);
        toast.success("Guest added.");
      }
      setDialogOpen(false);
      refetch();
      refetchNumbers();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving("");
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/client/guests/${deleting.id}`);
      toast.success("Guest removed.");
      setDeleting(null);
      refetch();
      refetchNumbers();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const onImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/client/guests/import", fd);
      toast.success(`${r.data.inserted} guest${r.data.inserted === 1 ? "" : "s"} imported.`);
      if (r.data.skipped) {
        toast.warning(`${r.data.skipped} row(s) skipped (empty name or not attending).`);
      }
      refetch();
      refetchNumbers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Import failed.");
    }
  };

  const onExport = async (fmt) => {
    try {
      const r = await api.get(`/client/guests/export?format=${fmt}`, { responseType: "blob" });
      downloadBlob(r.data, `gledswood-guest-list.${fmt}`);
      toast.success(`Guest list downloaded (${fmt.toUpperCase()}).`);
    } catch {
      toast.error("Download failed.");
    }
  };

  if (loading) {
    return <Loading label="Loading guests" />;
  }

  return (
    <div data-testid="guests-page">
      <PageHeader
        eyebrow="Your Event"
        title="Guests"
        description="Manage your guest list, track RSVPs and dietary requirements, and import or export your list."
      >
        <StatusBadge
          status={stored?.client_status}
          at={stored?.confirmed_at}
          by={stored?.confirmed_by}
          testid="guests-status-badge"
        />
        <input ref={fileRef} type="file" accept=".csv,.xlsx" hidden onChange={onImport} data-testid="import-file-input" />
        <Button variant="outline" className="rounded-full" data-testid="import-btn" onClick={() => fileRef.current?.click()}>
          <UploadSimple size={18} className="mr-1" /> Import
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="rounded-full" data-testid="export-btn">
              <DownloadSimple size={18} className="mr-1" /> Download
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onExport("csv")} data-testid="export-csv">CSV (.csv)</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport("xlsx")} data-testid="export-xlsx">Excel (.xlsx)</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button className="rounded-full bg-primary hover:bg-primary/90" onClick={openAdd} data-testid="add-guest-btn">
          <Plus size={18} className="mr-1" /> Add guest
        </Button>
      </PageHeader>

      {locked && (
        <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="guests-lock-banner">
          <CheckCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-emerald-600" />
          <div>
            <p className="text-sm text-primary">
              Your guest numbers are confirmed. Contact {EVENTS_EMAIL} to request changes.
            </p>
            <div className="mt-2">
              <ConfirmationStamp
                label="Confirmed"
                at={stored?.confirmed_at}
                by={stored?.confirmed_by}
                testid="guests-confirmed-stamp"
              />
            </div>
          </div>
        </div>
      )}

      <section className="mt-6 rounded-lg border border-border bg-card p-6" data-testid="guest-list-summary">
        <div className="flex flex-wrap items-baseline gap-3">
          <p className="font-heading text-5xl text-primary" data-testid="guest-list-total">{list.length}</p>
          <p className="text-sm text-muted-foreground">{list.length === 1 ? "guest" : "guests"} added</p>
        </div>
        {withDietary > 0 && (
          <p className="mt-1 text-sm text-secondary" data-testid="guest-list-dietary-count">
            {withDietary} with dietary requirements
          </p>
        )}
        <div className="mt-5 flex flex-wrap gap-2">
          {GUEST_TYPES.map((type) => (
            <span
              key={type.id}
              className="inline-flex items-baseline gap-2 rounded-full border border-border bg-muted/40 px-3 py-1"
              data-testid={`guest-list-chip-${type.id}`}
            >
              <span className="font-heading text-base text-primary">{typeCounts[type.id]}</span>
              <span className="text-xs uppercase tracking-wide text-muted-foreground">{type.label}</span>
            </span>
          ))}
        </div>
      </section>

      {list.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            icon={Users}
            title="No guests added yet"
            description="Add guests one by one, or import them from a spreadsheet."
            action={
              <div className="flex flex-wrap justify-center gap-3">
                <Button className="rounded-full bg-primary hover:bg-primary/90" onClick={openAdd} data-testid="guest-list-empty-add">
                  Add your first guest
                </Button>
                <Button variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} data-testid="guest-list-empty-import">
                  Import spreadsheet
                </Button>
              </div>
            }
          />
          <aside className="mt-5 flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-4" data-testid="guest-list-import-help">
            <Info size={18} className="mt-0.5 shrink-0 text-secondary" />
            <p className="text-xs text-muted-foreground">
              Supported formats: .xlsx, .xls or .csv. We look for columns named <strong>Name</strong>{" "}
              (or First/Last Name), <strong>Type</strong> (adult/teen/child/baby/vendor),{" "}
              <strong>Dietary</strong>, <strong>Notes</strong> and <strong>Table</strong>. Rows
              marked as not attending are skipped.
            </p>
          </aside>
        </div>
      ) : (
        <section className="mt-6 divide-y divide-border rounded-lg border border-border bg-card" data-testid="guest-list">
          {list.map((guest) => (
            <GuestRow key={guest.id} guest={guest} onEdit={openEdit} onDelete={setDeleting} />
          ))}
        </section>
      )}

      <div className="mt-8">
        <GuestNumbersPanel
          stored={stored}
          guests={list}
          locked={locked}
          defaultOpen={list.length === 0}
          onSave={async (payload, clientStatus) => {
            await api.put("/client/guest-numbers", { ...payload, client_status: clientStatus });
            refetchNumbers();
          }}
        />
      </div>

      <aside className="mt-8 flex flex-col gap-3 rounded-lg border border-border bg-muted/40 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="guest-send-panel">
        <p className="text-sm text-muted-foreground">
          Download your list as a CSV and email it to{" "}
          <a className="text-secondary hover:text-primary" href={`mailto:${EVENTS_EMAIL}`} data-testid="guest-events-email">
            {EVENTS_EMAIL}
          </a>
          . The portal does not send this email for you.
        </p>
        <Button variant="outline" className="rounded-full shrink-0" onClick={() => onExport("csv")} data-testid="download-csv-btn">
          <DownloadSimple size={16} className="mr-1" /> Download CSV
        </Button>
      </aside>

      <GuestFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        form={form}
        setForm={setForm}
        onSave={saveGuest}
        saving={saving === "guest"}
      />

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove guest?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {deleting?.name || "this guest"} from your guest list?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-destructive hover:bg-destructive/90" onClick={confirmDelete} data-testid="confirm-delete-guest">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
